/**
 * Access parts of our application state using pure functions.
 */
import Utils from 'src/utils/general';
import Project from 'src/api/project';
import { expandBotanicQueries } from 'src/utils/query';
import { expandThemeGroup } from 'src/pages/dashboard/Dashboard.utils';
// Auth
export const loggedIn = (state) => !!state.auth.token;
export const userName = (state) => {
    if (state.auth.user) {
        return state.auth.user.first_name + ' ' + state.auth.user.last_name;
    }
    return '';
};
export const currentUser = (state) => state.auth.user;
export const isAdmin = (state) => {
    if (state.auth.user) {
        return state.auth.user.admin;
    }
    return false;
};
export const isSubscribed = (state) => {
    if (!state.auth.user) {
        return null;
    }
    return state.subscription.allowsAccess;
};
export const isTrialing = (state) => {
    if (!state.auth.user) {
        return false;
    }
    return state.subscription.trialing;
};
export const subscribedUntil = (state) => {
    return state.subscription.termEnd;
};
export const dataUnits = (state) => {
    return state.subscription.dataUnitsBalance;
};
export const domain = (state) => {
    return state.subscription.domain;
};
export const plan = (state) => {
    return state.subscription.planId;
};
// Project
export const integrations = (state) => state.project.integrations;
export const notifications = (state) => state.project.notifications;
export const projects = (state) => state.project.projects;
export const currentProject = (state) => state.project.project;
export const currentAnalysis = (state) => state.project.analysis;
export const currentModel = (state) => state.project.model;
export const currentDashboard = (state) => state.project.dashboard;
export const dashboardWidgetConfig = (state) => state.project.dashboardWidgetConfig;
export const dashboardDateRange = (state) => state.project.dashboardDateRange;
export const dashboardQueries = (state) => state.project.dashboardQueries;
export const themeGroups = (state) => state.project.themeGroups;
export const dashboardAwaiting = (state) => state.project.dashboardAwaiting;
export const projectMessage = (state) => state.project.message;
export const analysisTimeline = (state) => state.project.analysisTimeline;
export const dashboardTimeseries = (state) => state.project.dashboardTimeseries;
export const nps = (state) => state.project.nps;
export const sentiment = (state) => {
    let percentages = {};
    let frequencies = {};
    if (!state || !state.project || !state.project.sentiment)
        return { percentages, frequencies };
    if (state.project.sentiment.percentages) {
        let { mixed, neutral, positive, negative } = state.project.sentiment.percentages;
        percentages = {
            mixed: !Number.isNaN(mixed) ? mixed : 0,
            neutral: !Number.isNaN(neutral) ? neutral : 0,
            positive: !Number.isNaN(positive) ? positive : 0,
            negative: !Number.isNaN(negative) ? negative : 0
        };
    }
    if (state.project.sentiment.frequencies) {
        const { mixed, neutral, positive, negative } = state.project.sentiment.frequencies;
        frequencies = {
            mixed: !Number.isNaN(mixed) ? mixed : 0,
            neutral: !Number.isNaN(neutral) ? neutral : 0,
            positive: !Number.isNaN(positive) ? positive : 0,
            negative: !Number.isNaN(negative) ? negative : 0
        };
    }
    return { frequencies, percentages };
};
export const queriesMetadata = (state) => state.project.queriesMetadata;
export const savedQueries = (state) => state.project.savedQueries;
export const expandedSavedQueries = (state, getters) => {
    return expandBotanicQueries(getters.savedQueries, getters.savedQueries);
};
export const expandedDashboardQueries = (state, getters) => {
    return expandBotanicQueries(getters.dashboardQueries, getters.savedQueries);
};
export const expandedThemeGroups = (state, getters) => {
    return getters.themeGroups.map((g) => expandThemeGroup(g, getters.savedQueries));
};
// Results
export const hasNumericFields = (state, getters) => {
    return !!getters.sortedFieldsUnlimited.find((f) => f.type === 2);
};
export const hasNPS = (state) => {
    let schema = state.project?.project?.schema ?? state.project?.dashboard?.project?.schema;
    if (schema) {
        return Utils.checkNpsExists(schema);
    }
    return false;
};
export const npsFields = (state) => {
    let schema = state.project?.project?.schema ?? state.project?.dashboard?.project?.schema;
    if (schema) {
        return Utils.getNpsFields(schema);
    }
    return [];
};
export const hasDate = (state) => {
    let schema = state.project?.project?.schema ?? state.project?.dashboard?.project?.schema;
    if (schema) {
        return Utils.checkSchemaHasDate(schema);
    }
    return false;
};
export const dateFields = (state) => {
    let schema = state.project?.project?.schema ?? state.project?.dashboard?.project?.schema;
    if (schema) {
        return Utils.getDateFields(schema);
    }
    return [];
};
export const defaultDateField = (state, getters) => {
    if (state.project?.analysis?.default_date_field) {
        return state.project.analysis.default_date_field;
    }
    return null;
};
/**
 * Returns a function that will return an array of schema fields in the
 * project that match a particular type. The possible types are defined in
 * `api/project` and are TEXT, NUMBER, DATE, DATE_TIME, LABEL,
 * BOOLEAN, NPS and IGNORE.
 *
 * The returned function will return a result that looks like this array:
 *
 *   [
 *     {
 *
 *       index: boolean,
 *       name: string,
 *       type: integer
 *     },
 *     ...
 *   ]
 *
 * @param state
 * @returns {function(string): ([Object]|undefined)}
 */
// https://vuex.vuejs.org/guide/getters.html#method-style-access
export const getFieldsForType = (state) => (type) => {
    let schema = state.project?.project?.schema ?? state.project?.dashboard?.project?.schema;
    if (!(schema)) {
        return;
    }
    const column_index = Project.COLUMN_LABELED_TYPES.get(type);
    const fields = schema.filter((field) => field.type === column_index);
    return fields;
};
export const categoricalFields = (state) => {
    return getFieldsForType(state)('LABEL');
};
/**
 * Return the name of the first field in the project schema that matches
 * the given "type". The possible types are defined in
 * `api/project` and are TEXT, NUMBER, DATE, DATE_TIME, LABEL,
 * BOOLEAN, NPS and IGNORE.
 *
 * If there are no fields in the schema matching the given type, null
 * is returned.
 *
 * @param state
 * @returns {null|String}
 */
export const getFirstSchemaFieldNameWithType = (state) => (type) => {
    const matchingSchemaFields = getFieldsForType(state)(type);
    if (!matchingSchemaFields) {
        return null;
    }
    const first_match = matchingSchemaFields[0];
    if (first_match) {
        return first_match.name;
    }
    return null;
};
export const hasSentiment = (state) => {
    if (state.project.analysis && state.project.model) {
        return state.project.model.attribute_info.sentiment != null;
    }
    return false;
};
// Route
export const currentRoute = (state) => state.route;
// Flags
export const featureFlags = (state) => state.featureflags.features;
export const lastFFlagFetchWhen = (state) => state.featureflags.lastFetchWhen;
